import { useState, useEffect, useMemo } from 'react';

/**
 * Hook to manage state-level simulation results
 * Fetches geography data and calculates "Pay vs Get" metrics
 */
export function useStateSimulation(selectedStateCode, config, eventsState = {}) {
    const [stateData, setStateData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch state data when code changes
    useEffect(() => {
        if (!selectedStateCode) {
            setStateData(null);
            return;
        }

        async function fetchStateData() {
            setIsLoading(true);
            setError(null);
            try {
                const startYear = 2026;
                const endYear = 2046;
                const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);
                const yearDataPromises = years.map(year =>
                    fetch(`/data/geography/${year}/${selectedStateCode}.json`)
                        .then(res => res.json())
                        .catch(err => {
                            console.warn(`Failed to fetch ${year} data for ${selectedStateCode}`, err);
                            return null;
                        })
                );

                const allYears = await Promise.all(yearDataPromises);
                setStateData(allYears.filter(d => d !== null));
            } catch (err) {
                console.error('State data fetch error:', err);
                setError('Failed to load state projection data.');
            } finally {
                setIsLoading(false);
            }
        }

        fetchStateData();
    }, [selectedStateCode]);

    // Calculate simulation results for the state
    const results = useMemo(() => {
        if (!stateData || stateData.length === 0) return null;

        const yearlyResults = stateData.map(yearJson => {
            const fp = yearJson.fiscal_projections;
            if (!fp) return null;

            const year = yearJson.year;

            // 1. Calculate Revenue (Pay)
            const employerRevenue = (fp.wages || 0) * config.employerRate;
            const individualRevenue = (fp.agi || 0) * config.baseIndividualRate;

            // Surtax calculation (Rich Tax)
            let surtaxRevenue = 0;
            if (config.surtaxRates && config.surtaxRates[0] > 0) {
                surtaxRevenue += (fp.agi_above_100k || 0) * config.surtaxRates[0];
            }
            if (config.surtaxRates && config.surtaxRates[1] > 0) {
                surtaxRevenue += (fp.agi_above_250k || 0) * config.surtaxRates[1];
            }
            if (config.surtaxRates && config.surtaxRates[2] > 0) {
                surtaxRevenue += (fp.agi_above_500k || 0) * config.surtaxRates[2];
            }

            const selfEmployedRevenue = (fp.self_employment_income || 0) * config.baseSelfEmployedRate;
            const totalTaxRevenue = employerRevenue + individualRevenue + surtaxRevenue + selfEmployedRevenue;

            // 2. Calculate Spending / Benefits (Get)
            // Basic claims spend
            let basicSpend = (fp.spend || 0);

            // Apply Policy Multipliers (Drugs, Admin)
            // Note: In national sim, spend is adjusted by drugPriceIndex etc.
            const drugEfficiency = (config.drugPriceIndex || 100) / 100;
            const adminEfficiency = (config.adminEfficiency || 100) / 100;

            // Scale the specific portions
            const adjustedDrugSpend = (fp.drug_spend || 0) * drugEfficiency;
            const adjustedAdminSpend = (fp.admin_spend || 0) * adminEfficiency;
            const hospitalPhysicianSpend = Math.max(0, basicSpend - (fp.drug_spend || 0) - (fp.admin_spend || 0));

            const totalProgramSpend = hospitalPhysicianSpend + adjustedDrugSpend + adjustedAdminSpend;

            // 3. Household Savings
            // We use the pre-calculated baseline savings from the data request (safecare_avg_savings * households)
            const households = yearJson.state_totals?.total_households || 0;
            const avgSavings = yearJson.state_totals?.safecare_avg_savings || 0;
            const householdSavings = avgSavings * households;

            // 4. Net Metrics (FIXED: Avoid double-counting)
            // "Get" should represent the fiscal transfer (Direct Program Spend)
            // "Household Savings" is a derivative economic benefit, kept separate
            const totalGet = totalProgramSpend;
            const netStimulus = totalGet - totalTaxRevenue;
            const payVsGetRatio = totalTaxRevenue > 0 ? totalGet / totalTaxRevenue : 0;

            return {
                year,
                pay: totalTaxRevenue,
                get: totalGet,
                netStimulus,
                payVsGetRatio,
                householdSavings,
                totalEconomicBenefit: totalGet + householdSavings, // For informational/advanced views
                programSpend: totalProgramSpend,
                taxRevenue: totalTaxRevenue,
                employerRevenue,
                individualRevenue,
                surtaxRevenue,
                selfEmployedRevenue
            };
        }).filter(r => r !== null);

        // Calculate 10-year aggregates vs Total Aggregates
        // The UI labels often say "10Yr", so we should provide a specific 10-year view.

        // Dynamic Range based on Config (e.g. 10, 15, 21)
        // Default to 10 if not specified to match initial UI state
        // Cap at 21 because that's the data limit
        const simYears = Math.min(config.simulationYears || 10, 21);
        const subsetDynamic = yearlyResults.slice(0, simYears);

        const totalPayDynamic = subsetDynamic.reduce((sum, r) => sum + (r.pay || 0), 0);
        const totalGetDynamic = subsetDynamic.reduce((sum, r) => sum + (r.get || 0), 0);
        const totalStimulusDynamic = subsetDynamic.reduce((sum, r) => sum + (r.netStimulus || 0), 0);
        const avgRatioDynamic = totalPayDynamic > 0 ? totalGetDynamic / totalPayDynamic : 0;

        // Full Horizon (21 Yr)
        const totalPayFull = yearlyResults.reduce((sum, r) => sum + (r.pay || 0), 0);
        const totalGetFull = yearlyResults.reduce((sum, r) => sum + (r.get || 0), 0);
        const totalStimulusFull = yearlyResults.reduce((sum, r) => sum + (r.netStimulus || 0), 0);
        const avgRatioFull = totalPayFull > 0 ? totalGetFull / totalPayFull : 0;

        // Validation for missing/zeroed segments
        const dataContinuity = yearlyResults.filter(r => r.get > 0).length / yearlyResults.length;

        return {
            yearlyResults: yearlyResults,
            summary: {
                // Summary respects config.simulationYears
                totalPay: totalPayDynamic,
                totalGet: totalGetDynamic,
                totalStimulus: totalStimulusDynamic,
                avgRatio: avgRatioDynamic,

                // Explicit 21Yr Data for advanced views
                totalPay21Yr: totalPayFull,
                totalGet21Yr: totalGetFull,
                totalStimulus21Yr: totalStimulusFull,
                avgRatio21Yr: avgRatioFull,

                dataContinuity
            }
        };
    }, [stateData, config]);

    return {
        results,
        isLoading,
        error
    };
}
